import {
  type GeneratedNodeAttribute,
  type InstanceContact,
  type InstanceDetail,
  type InstanceMember,
  type NodeAttributeValue,
  type ResponsiblePersonView,
  type ResponsibleRef,
  isResponsibleAttributeType,
  parseResponsibleAttributeValue,
} from "@keydials/api-client";
import { distinctInstanceMembers, memberLabel } from "./userAttribute";
import type { AttributeNodeScope } from "./fileAttribute";

/**
 * A Responsible person attribute (KEY-917) names ONE dial member or ONE of the dial's contacts, stored as
 * `user:<id>` / `contact:<id>`. Unlike every other field it is read with inheritance: a node with no answer
 * of its own shows the nearest one above it — the action point's, the section's, the dial's, then the parent
 * group dial's — which is the same rule the server applies (`IResponsiblePersonService.ResolveAsync`).
 */

/** What a Responsible field needs from the detail to work out the answer above it. */
export interface ResponsibleContext {
  /** The dial — what the invite history and Re-send are asked about. */
  instanceId: string;
  decls: readonly GeneratedNodeAttribute[];
  values: readonly NodeAttributeValue[];
  parent: ResponsiblePersonView | null;
  members: readonly InstanceMember[];
  contacts: readonly InstanceContact[];
}

export function responsibleContextOf(detail: InstanceDetail): ResponsibleContext {
  return {
    instanceId: detail.id,
    decls: detail.nodeAttributes ?? [],
    values: detail.nodeAttributeValues ?? [],
    parent: detail.parentResponsible ?? null,
    members: detail.members ?? [],
    contacts: detail.contacts ?? [],
  };
}

/** The answer a node inherits, ready to show. */
export interface ResponsibleAbove {
  /** Who — null when they are no longer a member or on the contacts list. */
  name: string | null;
  kind: ResponsibleRef["kind"];
  /** Where it was set, in words: "the action point", "the section", "the dial", "the group dial". */
  from: string;
}

type Level = "Item" | "Segment" | "Section" | "Dial";

const ABOVE: Record<Level, Level[]> = {
  Item: ["Segment", "Section", "Dial"],
  Segment: ["Section", "Dial"],
  Section: ["Dial"],
  Dial: [],
};

const FROM: Record<Level | "ParentDial", string> = {
  Item: "the item",
  Segment: "the action point",
  Section: "the section",
  Dial: "the dial",
  ParentDial: "the group dial",
};

/** The nearest answer at or above `levels[0]`, walking `levels` in order, then the parent group dial. */
function nearest(ctx: ResponsibleContext, scope: AttributeNodeScope, levels: readonly Level[]): { ref: ResponsibleRef; from: string } | null {
  for (const level of levels) {
    const keys = new Set(ctx.decls.filter((d) => d.target === level && isResponsibleAttributeType(d.type)).map((d) => d.key));
    if (keys.size === 0) continue;
    const hit = ctx.values.find((v) =>
      v.nodeType === level && keys.has(v.attributeKey)
      && (level === "Dial" || v.sectionKey === scope.sectionKey)
      && (level !== "Segment" && level !== "Item" || v.segmentKey === scope.segmentKey)
      && (level !== "Item" || v.itemKey === scope.itemKey));
    const ref = parseResponsibleAttributeValue(hit?.value);
    if (ref) return { ref, from: FROM[level] };
  }
  return ctx.parent
    ? { ref: { kind: ctx.parent.kind, id: ctx.parent.id.toLowerCase() }, from: FROM.ParentDial }
    : null;
}

/**
 * Who is responsible for the node at `scope` — its own answer, else the nearest above (KEY-918 reads this to
 * decide whether a "Responsible person only" status is the viewer's to set). The same rule as the server's
 * `IResponsiblePersonService.ResolveAsync`.
 */
export function responsibleAt(ctx: ResponsibleContext | undefined, scope: AttributeNodeScope): ResponsibleRef | null {
  if (!ctx) return null;
  const level = levelOf(scope);
  return nearest(ctx, scope, [level, ...ABOVE[level]])?.ref ?? null;
}

/** The level a scope addresses, as the panel builds scopes: an item key, else a segment key, else a section. */
function levelOf(scope: AttributeNodeScope): Level {
  if (scope.itemKey !== undefined) return "Item";
  if (scope.segmentKey) return "Segment";
  if (scope.sectionKey) return "Section";
  return "Dial";
}

/** Who a value names, by name — null when unset, malformed, or naming somebody no longer here. */
export function responsibleName(ref: ResponsibleRef | null, ctx: Pick<ResponsibleContext, "members" | "contacts">): string | null {
  if (!ref) return null;
  if (ref.kind === "Member") {
    const member = ctx.members.find((m) => m.userId.toLowerCase() === ref.id);
    return member ? memberLabel(member) : null;
  }
  return ctx.contacts.find((c) => c.id.toLowerCase() === ref.id)?.name ?? null;
}

/**
 * The nearest answer ABOVE the node at `scope` — what it shows while it has none of its own. Null when
 * nothing above names anybody. Only declarations of the Responsible kind count, so a stale value under a
 * key since re-authored as something else is never read as one.
 */
export function responsibleAbove(ctx: ResponsibleContext | undefined, scope: AttributeNodeScope): ResponsibleAbove | null {
  if (!ctx) return null;
  const found = nearest(ctx, scope, ABOVE[levelOf(scope)]);
  if (!found) return null;
  // The group dial's answer comes with its name: its people aren't on this dial's lists.
  const name = found.from === FROM.ParentDial ? ctx.parent?.name ?? null : responsibleName(found.ref, ctx);
  return { name, kind: found.ref.kind, from: found.from };
}

export interface ResponsibleState {
  ref: ResponsibleRef | null;
  name: string | null;
  /** The value names somebody no longer a member or on the list: kept as it was and flagged. */
  unresolved: boolean;
  memberOptions: InstanceMember[];
  contactOptions: InstanceContact[];
}

/** Resolves what a Responsible field shows and offers. */
export function responsibleState(value: string, members: readonly InstanceMember[] | undefined, contacts: readonly InstanceContact[] | null | undefined): ResponsibleState {
  const ctx = { members: members ?? [], contacts: contacts ?? [] };
  const ref = parseResponsibleAttributeValue(value);
  const name = responsibleName(ref, ctx);
  return {
    ref,
    name,
    unresolved: ref !== null && name === null,
    memberOptions: distinctInstanceMembers([...ctx.members]),
    contactOptions: [...ctx.contacts].sort((a, b) => a.order - b.order),
  };
}
